import type { MolyDatabase } from '../rxdb'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

export type ReplicationTask = {
  key: string
  label: string
  tier: 1 | 2 | 3
  setup?: () => Promise<any>
  skipMessage?: string
}

/**
 * Orchestrates tiered replication setup behind the RxDB leader-election gate.
 *
 * Only the leader tab runs replication: followers read from the shared
 * IndexedDB via RxDB's multiInstance broadcast, which means a single tab
 * performs the pull/push work for the whole browser.
 *
 * The manager is designed to be started once (on first leadership) and also
 * re-started mid-session if a follower tab is promoted to leader after the
 * current leader closes.
 */
export class ReplicationManager {
  private started = false
  private abortController: AbortController | null = null

  constructor(
    _db: MolyDatabase,
    private readonly tasks: Array<ReplicationTask>,
    private readonly states: Map<string, any>,
  ) {
    // db retained in signature for future hooks (e.g. leadership takeover
    // subscriptions on db.leaderElector()); currently unused.
    void _db
  }

  isStarted(): boolean {
    return this.started
  }

  async start(): Promise<void> {
    if (this.started) return
    this.started = true
    this.abortController = new AbortController()
    const signal = this.abortController.signal

    const tier1 = this.tasks.filter((t) => t.tier === 1)
    const tier2 = this.tasks.filter((t) => t.tier === 2)
    const tier3 = this.tasks.filter((t) => t.tier === 3)

    const run = async (task: ReplicationTask) => {
      if (!task.setup) {
        logger.logDebug(`[RxDB] Skipping ${task.label} (no setup)`)
        return null
      }
      logger.logDebug(`[RxDB] Setting up ${task.label} replication`)
      const state = await task.setup()
      logger.logDebug(`[RxDB] ${task.label} replication completed`)
      return { key: task.key, state }
    }

    logger.info(`[RxDB] Leader: starting Tier 1 (critical) replications: ${tier1.map((t) => t.key).join(', ')}`)
    const t1Start = performance.now()
    const tier1Results = await Promise.all(tier1.map(run))
    for (const result of tier1Results) {
      if (signal.aborted) return
      if (result) this.states.set(result.key, result.state)
    }
    logger.info(`[RxDB] Tier 1 completed in ${(performance.now() - t1Start).toFixed(0)}ms`)

    const startBackground = async () => {
      logger.info('[RxDB] Leader: starting Tier 2 (important) replications in background')
      const t2Start = performance.now()
      const tier2Results = await Promise.all(tier2.map(run))
      for (const result of tier2Results) {
        if (signal.aborted) return
        if (result) this.states.set(result.key, result.state)
      }
      logger.info(`[RxDB] Tier 2 completed in ${(performance.now() - t2Start).toFixed(0)}ms`)

      if (signal.aborted) return
      logger.info('[RxDB] Leader: starting Tier 3 (deferred) replications in background')
      const t3Start = performance.now()
      const tier3Results = await Promise.all(tier3.map(run))
      for (const result of tier3Results) {
        if (signal.aborted) return
        if (result) this.states.set(result.key, result.state)
      }
      logger.info(`[RxDB] Tier 3 completed in ${(performance.now() - t3Start).toFixed(0)}ms`)
      logger.info('[RxDB] All background replications completed')
    }

    startBackground().catch((err) => {
      if (!signal.aborted) {
        logger.logError(err as Error, LogCategory.SYNC, {
          phase: 'background',
          message: 'Background replication failed',
        })
      }
    })
  }

  async stop(): Promise<void> {
    if (!this.started) return
    this.abortController?.abort()
    this.abortController = null

    for (const [_name, replication] of this.states.entries()) {
      try {
        await replication.cancel()
      } catch (err) {
        logger.logError(err as Error, LogCategory.SYNC, {
          message: 'Failed to cancel replication',
        })
      }
    }
    this.states.clear()
    this.started = false
  }
}
